
import Vue, { PropType } from 'vue';
import CardCollapse from '@/components/common/CardCollapse.vue';
import ConfirmActionModal from '@/components/modal/ConfirmActionModal.vue';
import {
	CommissionData,
	Controller,
	LimboController,
	ControllerDeletion,
} from '@/interfaces/controllers';
import { TranslateResult } from 'vue-i18n';
import { DeviceType } from '@/interfaces/types';
import { SelectMenu } from '@/interfaces/global';
import { Room, Zone } from '@/interfaces/lighting';
import { Floor } from '@/interfaces/locations';

interface UncommissionedControllerEdit {
	select: boolean;
	newName: string;
	newFloor: string;
	newRoom: string;
	newZone: string;
	newType: string;
}

interface ComponentControllerData {
	controllers: {
		original: {
			type: 'controller' | 'limbo_controller';
			controller: LimboController | Controller;
		};
		form: UncommissionedControllerEdit;
	}[];
	formValid: boolean;
}

export default Vue.extend({
	components: { CardCollapse, ConfirmActionModal },
	data(): ComponentControllerData {
		return {
			controllers: [],
			formValid: true,
		};
	},
	props: {
		floors: {
			type: Array as PropType<Floor[]>,
			default: (): Floor[] => [],
		},
		rooms: {
			type: Array as PropType<Room[]>,
			default: (): Room[] => [],
		},
		zones: {
			type: Array as PropType<Zone[]>,
			default: (): Zone[] => [],
		},
		deviceTypes: {
			type: Array as PropType<DeviceType[]>,
			default: (): DeviceType[] => [],
		},
		uncommissionedControllers: { type: Array as PropType<(Controller | LimboController)[]> },
		userPermission: { type: Boolean, default: true },
	},
	computed: {
		controllersStatus(): TranslateResult {
			if (!this.controllers || this.controllers.length === 0) {
				return this.$i18n.t('There are no uncommissioned devices');
			}

			return this.$i18n.tc('uncommissioned-devices-total', Math.min(2, this.controllers.length), {
				count: this.controllers.length,
			});
		},

		floorSelection(): SelectMenu[] {
			const floorSelection: SelectMenu[] = [{ value: '', text: this.$i18n.t('None') }];

			return floorSelection.concat(
				this.floors.map((floor: Floor) => {
					return {
						value: floor.id,
						text: floor.name,
					};
				})
			);
		},

		deviceTypeSelection(): SelectMenu[] {
			return this.deviceTypes.map((deviceType: DeviceType) => {
				return {
					value: deviceType.id,
					text: deviceType.name,
				};
			});
		},
	},
	watch: {
		uncommissionedControllers: {
			handler(controllers: (Controller | LimboController)[]) {
				let fetchedControllers: (Controller | LimboController)[] = [];
				if (controllers.length === 0) {
					fetchedControllers = [];
				} else if (controllers.length === 1) {
					fetchedControllers = controllers;
				} else {
					fetchedControllers = controllers.sort((a, b) => a.guid.localeCompare(b.guid));
				}

				this.controllers = fetchedControllers.map((controller) => {
					return {
						original: {
							type:
								'fk_client' in controller && controller.fk_client
									? 'controller'
									: 'limbo_controller',
							controller: controller,
						},
						form: {
							select: false,
							newName: '',
							newFloor: '',
							newRoom: '',
							newZone: '',
							newType: '',
						},
					};
				});
			},
			immediate: true,
		},
	},
	methods: {
		checkAllCommissions(check: boolean): void {
			for (const controller of this.controllers) {
				controller.form.select = check;
			}
		},

		filterRoomSelection(selectedFloor: string): SelectMenu[] {
			if (!selectedFloor) {
				return [];
			}

			const roomSelection: SelectMenu[] = [{ value: '', text: this.$i18n.t('None') }];

			return roomSelection.concat(
				this.rooms
					.filter((room) => room.fk_area === Number(selectedFloor))
					.map((room) => {
						return {
							value: room.id,
							text: room.name,
						};
					})
			);
		},

		filterZoneSelection(selectedRoom: string): SelectMenu[] {
			if (!selectedRoom) {
				return [];
			}

			const zoneSelection: SelectMenu[] = [{ value: '', text: this.$i18n.t('None') }];

			return zoneSelection.concat(
				this.zones
					.filter((zone) => zone.fk_room === selectedRoom)
					.map((zone) => {
						return {
							value: zone.id,
							text: zone.name,
						};
					})
			);
		},

		areCheckboxesSelected(): boolean {
			return !this.controllers.some((controller) => controller.form.select);
		},

		checkFloorChange(index: string): void {
			this.controllers[Number(index)].form.newRoom = '';
			this.controllers[Number(index)].form.newZone = '';
			Vue.set(this.controllers, index, this.controllers[Number(index)]);
		},

		checkRoomChange(index: string): void {
			this.controllers[Number(index)].form.newZone = '';
			Vue.set(this.controllers, index, this.controllers[Number(index)]);
		},

		nameValidation(name: string, selectStatus: boolean): boolean | null {
			if (!selectStatus) {
				return null;
			}

			return !!(name && name.length >= 3);
		},

		selectValidation(selectedItem: string, selectStatus: boolean): boolean | null {
			if (!selectStatus) {
				return null;
			}

			return !!selectedItem;
		},

		validate(): boolean {
			return this.controllers.every((controller) => {
				if (!controller.form.select) {
					return true;
				}

				return !!(
					this.nameValidation(controller.form.newName, true) &&
					this.selectValidation(controller.form.newType, true)
				);
			});
		},

		confirmCommission(): void {
			if (this.validate()) {
				this.formValid = true;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.commissionDevicesConfirmModal as any).show();
			} else {
				this.formValid = false;
			}
		},

		confirmDelete(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.confirmActionModal as any).show();
		},

		submitCommission(): void {
			const commissionList: CommissionData[] = this.controllers
				.filter((controller) => controller.form.select)
				.map((controller) => {
					const commission: CommissionData = {
						category: '',
						apiEndpoint: '',
						name: controller.form.newName,
						fk_device_type: controller.form.newType,
						type: controller.original.type,
						guid: controller.original.controller.guid,
						id: controller.original.controller.id,
					};
					if (controller.form.newFloor) {
						commission.fk_area = controller.form.newFloor;
					}
					if (controller.form.newRoom && controller.form.newZone) {
						commission.fk_room = controller.form.newRoom;
						commission.fk_zone = controller.form.newZone;
					}

					if (controller.original.type === 'limbo_controller') {
						commission.gateway_id = (controller.original.controller as LimboController).gateway_id;
					}

					return commission;
				});

			if (commissionList.length !== 0) {
				this.$emit('commissionDevices', commissionList);
				this.closeCommissionModal();
			}
		},

		async submitDeviceDeletion(): Promise<void> {
			const deleteList: ControllerDeletion[] = this.controllers
				.filter((controller) => controller.form.select)
				.map((controller) => {
					let deleteControllers: ControllerDeletion;

					if (controller.original.type === 'limbo_controller') {
						deleteControllers = {
							id: controller.original.controller.id,
							gateway_id: (controller.original.controller as LimboController).gateway_id,
							guid: controller.original.controller.guid,
							type: controller.original.type,
						};
					} else {
						deleteControllers = {
							id: controller.original.controller.id,
							guid: controller.original.controller.guid,
							type: controller.original.type,
						};
					}

					return deleteControllers;
				});
			this.$emit('deleteUncommissionedDevices', deleteList);
			this.closeDeletionModal();
		},

		closeCommissionModal(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.commissionDevicesConfirmModal as any).hide();
		},
		closeDeletionModal(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.confirmActionModal as any).hide();
		},
	},
});
